import type { BlockKind } from '@/blocks/domain/types/BlockKind';
import type { Block, BlockFieldMedia, BlockFieldOrientation, BlockFieldStyle, BlockStyle, BlockVersion } from '@/blocks/domain/types';
import type { HeaderContent, SectionLayout } from '@/blocks/domain/types/BlockContent/HeaderContent';
import type { CustomerBlockContent } from '@/blocks/domain/types/BlockContent/CustomerBlockContent';
import type { CustomContent } from '@/blocks/domain/types/BlockContent/CustomContent';
import type {
  CommentsContent,
  CustomTextContent,
  ScopeOfWorkContent,
  TermsConditionsContent
} from '@/blocks/domain/types/BlockContent/ProseContent';
import type { ScopeFieldsContent } from '@/blocks/domain/types/BlockContent/ScopeFieldsContent';
import type { LineItemsTableContent } from '@/blocks/domain/types/BlockContent/LineItemsTableContent';
import type { TotalContent } from '@/blocks/domain/types/BlockContent/TotalContent';
import type { RolesResponsibilitiesContent } from '@/blocks/domain/types/BlockContent/RolesResponsibilitiesContent';
import type { SignatureContent } from '@/blocks/domain/types/BlockContent/SignatureContent';
import type { PriceBookContent } from '@/blocks/domain/types/BlockContent/PriceBookContent';
import type { SpacerContent } from '@/blocks/domain/types/BlockContent/SpacerContent';
import type { CoverPageContent } from '@/blocks/domain/types/BlockContent/CoverPageContent';

import { fromBlockStyle, mapStyle } from '@/blocks/application/mappers/blockStyleMapper';
import {
  fromCommentsContent,
  fromCoverPageContent,
  fromCustomContent,
  fromCustomTextContent,
  fromCustomerBlockContent,
  fromHeaderContent,
  fromLineItemsTableContent,
  fromPriceBookContent,
  fromRolesResponsibilitiesContent,
  fromScopeFieldsContent,
  fromScopeOfWorkContent,
  fromSignatureContent,
  fromSpacerContent,
  fromTotalContent,
  fromTermsConditionsContent,
  mapCommentsContent,
  mapCoverPageContent,
  mapCustomContent,
  mapCustomTextContent,
  mapCustomerBlockContent,
  mapHeaderContent,
  mapLineItemsTableContent,
  mapPriceBookContent,
  mapRolesResponsibilitiesContent,
  mapScopeFieldsContent,
  mapScopeOfWorkContent,
  mapSignatureContent,
  mapSpacerContent,
  mapTotalContent,
  mapTermsConditionsContent
} from '@/blocks/application/mappers/content';
import { resolveBlockLifecycleStatus } from '@/blocks/application/sieve/buildBlocksSieveFilter';
import { resolveBlockName } from '@/blocks/domain/helpers/resolveBlockName';
import type { BlockContent } from '@/blocks/domain/types/BlockContent/BlockContent';
import type { LineItemsColumn } from '@/blocks/domain/types/BlockContent/LineItemsTableContent';
import type { PriceBookColumn } from '@/blocks/domain/types/BlockContent/PriceBookContent';

/**
 * DTO interfaces (temporary until Orval models ship fully).
 * We keep these shapes aligned with the API contract so mapper logic + tests
 * remain meaningful during the Phase 1 stub period.
 */

export interface BlockDto {
  blockId: string;
  kind: BlockKind;
  /** Never blank in a current response (the API defaults it to the kind's display name), but legacy rows may be. */
  name?: string | null;
  description: string | null;
  currentVersionId: string | null;
  currentVersion?: number;
  status?: string;
  isArchived?: boolean;
  archivedAt?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BlockStyleDto {
  fontSize: number;
  fontSizePt?: number;
  fontWeight: 'normal' | 'bold';
  fontWeightValue?: number;
  textAlign: 'left' | 'center' | 'right';
  textColor: string;
  backgroundColor: string;
  paddingY: number;
  borderTop: boolean;
  borderBottom: boolean;
  italic?: boolean;
  uppercase?: boolean;
  previewHeight?: number;
  fontFamily?: string;
  borderColor?: string;
}

/** snake_case mirror of BlockField. The renderer / mapper convert both ways. */
export type TableColumnDto = {
  key: string;
  label: string;
  visible: boolean;
  sortOrder: number;
};

/**
 * snake_case mirror of BlockField. The renderer / mapper convert both ways.
 *
 * `style` carries the full 12-value `BlockFieldStyle` contract (LLAMS-610 Part 7) — the backend
 * stores block content JSON verbatim (shape + a 5 MB limit only, per the epic's backend-dependency
 * note), so widening this type is a frontend-only change, not a backend contract change.
 */
export type BlockFieldDto = {
  id: string;
  label: string;
  value: string;
  width: 'full' | 'half';
  align: 'left' | 'center' | 'right';
  style: BlockFieldStyle;
  visible: boolean;
  sort_order: number;
  label_visible?: boolean;
  value_visible?: boolean;
  name?: string;
  orientation?: BlockFieldOrientation;
  column?: number;
  section_id?: string;
  media?: BlockFieldMedia;
  italic?: boolean;
  uppercase?: boolean;
};

export type BlockContentDto =
  | {
      kind: 'header';
      fields: BlockFieldDto[];
      columns?: number;
      sections?: Array<{ id: string; layout: SectionLayout }>;
    }
  | {
      kind: 'customer_block';
      fields: BlockFieldDto[];
      columns?: number;
      sections?: Array<{ id: string; layout: SectionLayout }>;
    }
  | {
      kind: 'scope_of_work' | 'comments' | 'terms_conditions' | 'custom_text';
      title: string;
      body_html: string;
      pageBreakBefore?: boolean;
    }
  | {
      kind: 'custom';
      title: string;
      body_html: string;
      /** Sectioned Custom block (LLAMS-610): the same field + section model a header carries. Absent on legacy prose. */
      fields?: BlockFieldDto[];
      sections?: Array<{ id: string; layout: SectionLayout }>;
      pageBreakBefore?: boolean;
    }
  | {
      kind: 'scope_fields';
      title: string;
    }
  | {
      kind: 'line_items_table';
      title: string;
      columns: Array<TableColumnDto & { key: LineItemsColumn['key'] }>;
      show_rate_schedule_note: boolean;
    }
  | {
      kind: 'total';
      show_subtotal: boolean;
      show_adjustments: boolean;
      grand_total_label: string;
      placement?: 'left' | 'right';
      heading?: string;
      subtotal_label?: string;
      discount_label?: string;
      surcharge_label?: string;
      tax_label?: string;
      row_order?: string[];
    }
  | {
      kind: 'roles_responsibilities';
      title: string;
      rows: Array<{ id?: string; role: string; responsibility: string; hidden?: boolean }>;
      categories?: Array<{
        id: string;
        name: string;
        headingVisible?: boolean;
        rows: Array<{ id: string; text: string; party: 'customer' | 'company' | 'shared'; partyLabel?: string; hidden?: boolean }>;
      }>;
      // Backend may return legacy array form; we map to the domain's role/responsibility object.
      columns:
        | Array<TableColumnDto & { key: 'role' | 'responsibility' }>
        | {
            role?: { visible: boolean; label: string };
            responsibility?: { visible: boolean; label: string };
          };
    }
  | {
      kind: 'signature';
      fields: BlockFieldDto[];
      sections?: Array<{ id: string; layout: SectionLayout }>;
    }
  | {
      kind: 'price_book';
      title: string;
      service_item_ids: string[];
      columns: Array<TableColumnDto & { key: PriceBookColumn['key'] }>;
    }
  | {
      kind: 'spacer';
      height_px: number;
    }
  | {
      kind: 'cover_page';
      title: string;
      show_logo: boolean;
      show_customer_name: boolean;
      show_date: boolean;
    };

export interface BlockVersionDto {
  blockVersionId: string;
  blockId: string;
  versionNumber: number;
  content: BlockContentDto;
  style: BlockStyleDto;
  blockJson: unknown;
  publishedAt: string | null;
  createdAt: string;
  /** Wire lifecycle status (`Draft` / `Published` / `Archived`) when the payload carries it. */
  status?: string;
  /** `createdBy.displayName` from the version payload. */
  authorDisplayName?: string | null;
  /** The published version this one was restored from, when the payload carries it. */
  revertedFromVersionNumber?: number | null;
}

export interface BlockUpdateDto {
  content: BlockContentDto;
  style: BlockStyleDto;
}

function mapContent(dto: BlockContentDto): BlockContent {
  const toContentDispatch: Record<BlockKind, (contentDto: BlockContentDto) => BlockContent> = {
    header: (contentDto) => mapHeaderContent(contentDto as Parameters<typeof mapHeaderContent>[0]),
    customer_block: (contentDto) => mapCustomerBlockContent(contentDto as Parameters<typeof mapCustomerBlockContent>[0]),
    scope_of_work: (contentDto) => mapScopeOfWorkContent(contentDto as Parameters<typeof mapScopeOfWorkContent>[0]),
    scope_fields: (contentDto) => mapScopeFieldsContent(contentDto as Parameters<typeof mapScopeFieldsContent>[0]),
    line_items_table: (contentDto) => mapLineItemsTableContent(contentDto as Parameters<typeof mapLineItemsTableContent>[0]),
    total: (contentDto) => mapTotalContent(contentDto as Parameters<typeof mapTotalContent>[0]),
    comments: (contentDto) => mapCommentsContent(contentDto as Parameters<typeof mapCommentsContent>[0]),
    terms_conditions: (contentDto) => mapTermsConditionsContent(contentDto as Parameters<typeof mapTermsConditionsContent>[0]),
    roles_responsibilities: (contentDto) =>
      mapRolesResponsibilitiesContent(contentDto as Parameters<typeof mapRolesResponsibilitiesContent>[0]),
    signature: (contentDto) => mapSignatureContent(contentDto as Parameters<typeof mapSignatureContent>[0]),
    price_book: (contentDto) => mapPriceBookContent(contentDto as Parameters<typeof mapPriceBookContent>[0]),
    custom_text: (contentDto) => mapCustomTextContent(contentDto as Parameters<typeof mapCustomTextContent>[0]),
    custom: (contentDto) => mapCustomContent(contentDto as Parameters<typeof mapCustomContent>[0]),
    spacer: (contentDto) => mapSpacerContent(contentDto as Parameters<typeof mapSpacerContent>[0]),
    cover_page: (contentDto) => mapCoverPageContent(contentDto as Parameters<typeof mapCoverPageContent>[0])
  };

  return toContentDispatch[dto.kind](dto);
}

export function toBlock(dto: BlockDto): Block {
  const status = resolveBlockLifecycleStatus(dto);
  return {
    blockId: dto.blockId,
    kind: dto.kind,
    name: resolveBlockName(dto.name, { kind: dto.kind }),
    description: dto.description,
    currentVersionId: dto.currentVersionId,
    currentVersion: dto.currentVersion ?? 0,
    status,
    isActive: dto.isActive,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt
  };
}

export function toBlockVersion(dto: BlockVersionDto): BlockVersion {
  return {
    blockVersionId: dto.blockVersionId,
    blockId: dto.blockId,
    versionNumber: dto.versionNumber,
    content: mapContent(dto.content),
    style: mapStyle(dto.style),
    blockJson: dto.blockJson,
    publishedAt: dto.publishedAt,
    createdAt: dto.createdAt,
    ...(dto.status !== undefined ? { status: dto.status } : {}),
    ...(dto.authorDisplayName !== undefined ? { authorDisplayName: dto.authorDisplayName } : {}),
    ...(dto.revertedFromVersionNumber !== undefined ? { revertedFromVersionNumber: dto.revertedFromVersionNumber } : {})
  };
}

export type BlockUpdateDomain = {
  content: BlockContent;
  style: BlockStyle;
};

const fromContentDispatch: Record<BlockKind, (content: BlockContent) => BlockContentDto> = {
  header: (content) => fromHeaderContent(content as HeaderContent),
  customer_block: (content) => fromCustomerBlockContent(content as CustomerBlockContent),
  scope_of_work: (content) => fromScopeOfWorkContent(content as ScopeOfWorkContent),
  scope_fields: (content) => fromScopeFieldsContent(content as ScopeFieldsContent),
  line_items_table: (content) => fromLineItemsTableContent(content as LineItemsTableContent),
  total: (content) => fromTotalContent(content as TotalContent),
  comments: (content) => fromCommentsContent(content as CommentsContent),
  terms_conditions: (content) => fromTermsConditionsContent(content as TermsConditionsContent),
  roles_responsibilities: (content) => fromRolesResponsibilitiesContent(content as RolesResponsibilitiesContent),
  signature: (content) => fromSignatureContent(content as SignatureContent),
  price_book: (content) => fromPriceBookContent(content as PriceBookContent),
  custom_text: (content) => fromCustomTextContent(content as CustomTextContent),
  custom: (content) => fromCustomContent(content as CustomContent),
  spacer: (content) => fromSpacerContent(content as SpacerContent),
  cover_page: (content) => fromCoverPageContent(content as CoverPageContent)
};

export function fromBlockUpdate(domain: BlockUpdateDomain): BlockUpdateDto {
  return {
    content: fromContentDispatch[domain.content.kind](domain.content),
    style: fromBlockStyle(domain.style)
  };
}

export function serializeBlockJson(domain: BlockUpdateDomain): string {
  return JSON.stringify(fromBlockUpdate(domain));
}
